/**
 * NotificationBell Component - Epic 47
 *
 * Main notification bell component with dropdown.
 * Only visible for MASTER users.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Bell, X, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../hooks/useNotifications';
import NotificationItem from './NotificationItem';

interface NotificationBellProps {
  isCollapsed: boolean;
  isMaster: boolean;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  isCollapsed,
  isMaster,
}) => {
  const navigate = useNavigate();
  const {
    notificacoes,
    unreadCount,
    loading,
    error,
    isDropdownOpen,
    setIsDropdownOpen,
    toggleLeitura,
    clearError,
  } = useNotifications(isMaster);

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
        setExpandedId(null);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, setIsDropdownOpen]);

  const handleToggleDropdown = useCallback(() => {
    setIsDropdownOpen(!isDropdownOpen);
    if (!isDropdownOpen) {
      setExpandedId(null);
    }
  }, [isDropdownOpen, setIsDropdownOpen]);

  const handleToggleExpand = useCallback((id: number) => {
    setExpandedId(prev => prev === id ? null : id);
  }, []);

  const handleViewJob = useCallback((jobId: number) => {
    setIsDropdownOpen(false);
    navigate(`/contagens/${jobId}`);
  }, [navigate, setIsDropdownOpen]);

  const handleToggleLeitura = useCallback(async (id: number) => {
    await toggleLeitura(id);
  }, [toggleLeitura]);

  // Don't render if not MASTER
  if (!isMaster) {
    return null;
  }

  return (
    <div className="relative group">
      {/* Bell Button */}
      <button
        ref={buttonRef}
        onClick={handleToggleDropdown}
        className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg transition-colors ${
          isDropdownOpen
            ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
            : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
        } ${isCollapsed ? 'justify-center' : ''}`}
        aria-label={`Notificações${unreadCount > 0 ? ` (${unreadCount} não lidas)` : ''}`}
      >
        {/* Bell Icon with Badge */}
        <div className="relative flex-shrink-0">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold bg-red-500 text-white rounded-full px-1">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </div>

        {/* Label (when expanded) */}
        <span className={`text-sm font-medium whitespace-nowrap transition-all duration-300 ${
          isCollapsed ? 'w-0 opacity-0 overflow-hidden' : 'w-auto opacity-100'
        }`}>
          Notificações
          {unreadCount > 0 && (
            <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
              ({unreadCount})
            </span>
          )}
        </span>
      </button>

      {/* Tooltip (when collapsed) */}
      {isCollapsed && !isDropdownOpen && (
        <div
          className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-2 bg-gray-900 dark:bg-gray-800 text-white text-sm font-medium rounded-md shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[200] pointer-events-none"
          role="tooltip"
        >
          Notificações {unreadCount > 0 && `(${unreadCount})`}
          <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45"></div>
        </div>
      )}

      {/* Dropdown */}
      {isDropdownOpen && (
        <div
          ref={dropdownRef}
          className="absolute left-full bottom-0 ml-2 w-80 max-h-[500px] bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-[300] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Notificações
            </h3>
            <button
              onClick={() => setIsDropdownOpen(false)}
              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              aria-label="Fechar"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[400px]">
            {loading && notificacoes.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : error ? (
              <div className="p-4 text-center">
                <p className="text-sm text-red-500 dark:text-red-400 mb-2">
                  {error}
                </p>
                <button
                  onClick={clearError}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Tentar novamente
                </button>
              </div>
            ) : notificacoes.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Nenhuma notificação
                </p>
              </div>
            ) : (
              <div>
                {notificacoes.map((notificacao) => (
                  <NotificationItem
                    key={notificacao.id}
                    notificacao={notificacao}
                    isExpanded={expandedId === notificacao.id}
                    onToggleExpand={() => handleToggleExpand(notificacao.id)}
                    onToggleLeitura={handleToggleLeitura}
                    onViewJob={handleViewJob}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer with count */}
          {notificacoes.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                {unreadCount > 0
                  ? `${unreadCount} de ${notificacoes.length} não lidas`
                  : `${notificacoes.length} notificações`}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
